import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useDefaultEditorStyle,
  getComponentById,
  updateImageByMeta,
  updateImageByName,
  withInitialCropData,
  updateImageByExternal,
  getDataSourceByKey,
  DATA_SOURCE_KEY_MAPPING,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  SmartImageEditor,
  useExistingUploadedImage,
  useUploadService,
  ImageOptionsKey,
} from '@bingads-webui-campaign-react/smart-component-image';
import {
  useSiteConfig,
  useIsSmartPageM365StockImageEnabled,
  editorStatusLoadingWrapperForPromise,
  useEditorStatusContext,
  useMobileSupportEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getImageOptionsConfig } from './components/image-options';
import { getImageOptionsConfigForPivot } from './components/image-options-for-pivot';
import { proxyReadCoverComponentConfig } from './component-config-reader';


const getImageFromFacebookInfo = fbInfo => fbInfo.coverImage;

const UnstyledSmartCoverImageEditor = ({
  classes,
  i18n,
  componentId,
  gotoSettings,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    editorImgsContentClassName,
    editorPivotStyleContentClassName,
  } = useDefaultEditorStyle();
  const isSmartPageM365StockImageEnabled = useIsSmartPageM365StockImageEnabled();
  const { bulkUploadImagesByUrlsAsync } = useUploadService();
  const [, dispatchEditorStatus] = useEditorStatusContext();
  const displayAsPivot = isSmartPageM365StockImageEnabled;
  const isMobile = useMobileSupportEnabled();

  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const businessCategories =
    getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.BusinessCategories);

  const componentConfig = getComponentById(siteConfig, componentId);
  const {
    imageName,
    fileName,
    imageId,
    externalSrc,
  } = proxyReadCoverComponentConfig(componentConfig);

  const {
    selectedImageOption,
    setSelectedImageOption,
    facebookImageInfo,
    setFacebookImageInfo,
    uploadedFileInfo,
    setUploadedFileInfo,
    enableFacebookImport,
    stockImageInfo,
    setStockImageInfo,
  } = useExistingUploadedImage({
    imageId,
    fileName,
    externalSrc,
  });

  const dispatchConfigWithPayload = (payload) => {
    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload,
    });
  };

  const onNewImageUploaded = (imageMetaData, fileObj) => {
    dispatchSiteConfig({
      type: 'UPDATE_IMAGES',
      payload: {
        imageIdsToRemove: imageId && [imageId],
        imagesToAdd: [imageMetaData],
      },
    });

    const imageMetaDataWithCropData = withInitialCropData(imageMetaData);

    if (fileObj) {
      setUploadedFileInfo({
        file: fileObj,
        image: imageMetaDataWithCropData,
      });
      dispatchConfigWithPayload(updateImageByMeta(
        componentConfig,
        imageMetaDataWithCropData,
        fileObj
      ));
    } else {
      setFacebookImageInfo(imageMetaDataWithCropData);
      dispatchConfigWithPayload(updateImageByMeta(componentConfig, imageMetaDataWithCropData));
    }
  };

  const onStockImageItemSelectionChanged = (item) => {
    if (!stockImageInfo || stockImageInfo.externalSrc !== item.externalSrc) {
      dispatchConfigWithPayload(updateImageByExternal(componentConfig, item.externalSrc));
      setStockImageInfo({ externalSrc: item.externalSrc, image: null });
    }
  };

  const onUseStockImageOption = (info) => {
    dispatchConfigWithPayload(updateImageByExternal(componentConfig, info.externalSrc));
  };

  const onDefaultImageSelected = (selectedImageName) => {
    dispatchConfigWithPayload(updateImageByName(componentConfig, selectedImageName));

    // Remove old asset from other option
    dispatchSiteConfig({
      type: 'UPDATE_IMAGES',
      payload: {
        imageIdsToRemove: imageId && [imageId],
      },
    });
  };

  const imageOptionsConfig = displayAsPivot
    ? getImageOptionsConfigForPivot({
      i18n,
      onStockImageItemSelectionChanged,
      defaultKeywords: businessCategories,
      enableFacebookImport,
      gotoSettings,
      classes: {
        editorPivotStyleContentClassName,
      },
      facebookImageInfo,
    })
    : getImageOptionsConfig({ onDefaultImageSelected, imageName, i18n });

  const postUploadStockImage = useCallback(() => {
    if (selectedImageOption === ImageOptionsKey.DEFAULT
      && stockImageInfo
      && !stockImageInfo.image
      && stockImageInfo.externalSrc
    ) {
      const srcUrl = stockImageInfo.externalSrc;

      return bulkUploadImagesByUrlsAsync([srcUrl])
        .then(([imageMeta]) => {
          const { IdString: imageIdFromMeta } = imageMeta;

          dispatchSiteConfig({
            type: 'UPDATE_COVER_IMAGE',
            payload: { imageId: imageIdFromMeta },
          });

          dispatchSiteConfig({
            type: 'UPDATE_IMAGES',
            payload: {
              imageIdsToRemove: imageId && [imageId],
              imagesToAdd: [imageMeta],
            },
          });

          setStockImageInfo({ externalSrc: stockImageInfo.externalSrc, image: imageMeta });

          return imageMeta;
        })
        .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus));
    }

    return Promise.resolve(null);
  }, [
    bulkUploadImagesByUrlsAsync,
    dispatchSiteConfig,
    selectedImageOption,
    stockImageInfo,
    imageId,
    dispatchEditorStatus,
    setStockImageInfo,
  ]);

  useEffect(() => {
    dispatchEditorStatus({
      type: 'SET_ON_EXIT_COMPONENT_EDITING_EFFECT',
      payload: postUploadStockImage,
    });
  }, [postUploadStockImage, dispatchEditorStatus]);

  return (
    <div>
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Cover image'))}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('Choose a cover image that represents your business. For the best appearance, we recommend using a high-resolution image of 1080 x 608 pixels.'))}
      </div>
      <div className={editorImgsContentClassName}>
        <SmartImageEditor
          componentId={componentId}
          i18n={i18n}
          className={classNames({
            [classes.mobileStyle]: isMobile,
            'smart-page-cover-image-editor': true,
          })}
          getImageFromFacebookInfo={getImageFromFacebookInfo}
          imageOptionsConfig={imageOptionsConfig}
          label={i18n.getString(_TL_('Choose cover image'))}
          onNewImageUploaded={onNewImageUploaded}
          selectedKey={selectedImageOption}
          uploadedFileInfo={uploadedFileInfo}
          facebookImageInfo={facebookImageInfo}
          stockImageInfo={stockImageInfo}
          onUseStockImageOption={onUseStockImageOption}
          onChoiceChange={setSelectedImageOption}
          enableFacebookImport={enableFacebookImport}
          displayAsPivot={displayAsPivot}
        />
      </div>
    </div>
  );
};

UnstyledSmartCoverImageEditor.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
  gotoSettings: PropTypes.func.isRequired,
};

export const SmartCoverImageEditor = withDefaultStyles(
  UnstyledSmartCoverImageEditor,
  () => ({
    mobileStyle: {
      '@media (max-width: 600px)': {
        paddingRight: 15,
      },
    },
  })
);
